import type { Theme } from "@glideapps/glide-data-grid";
import { getAiSpreadsheetCellDisplayValue } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/cell-display-value";
import {
  contrastRatio,
  flattenColor,
  pickContrastingColor,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer/color-contrast";
import {
  isBufferedOptimisticManualCell,
  isOptimisticManualCell,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer/optimistic-manual-row-edits";
import {
  volcaColor,
  WORKBOOK_PAPER,
} from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-theme";
import { getCellErrorCode } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/utils";
import { isFormulaCell } from "@/payroll/ai-script/cell-kind-utils";
import {
  AI_SPREADSHEET_OVERRIDE_OUTCOME,
  AI_SPREADSHEET_VALUE_TYPE,
  isAiSpreadsheetNumericValueType,
  type AiSpreadsheetValueType,
  type IAiSpreadsheetCell,
  type CachedSpreadsheetCell,
  type IAiSpreadsheetCellOverride,
} from "@/payroll/ai-script/types";

export const isCellEditable = (cell: IAiSpreadsheetCell): boolean =>
  cell.locked !== true &&
  (!isOptimisticManualCell(cell) || isBufferedOptimisticManualCell(cell));

export const hasSuggestedValueOptions = (
  cell: IAiSpreadsheetCell,
): cell is IAiSpreadsheetCell & { suggested_values: string[] } =>
  !isFormulaCell(cell) &&
  cell.value_type === AI_SPREADSHEET_VALUE_TYPE.text &&
  Array.isArray(cell.suggested_values) &&
  cell.suggested_values.length > 0;

export type CellContentAlign = "left" | "center" | "right";

// WCAG's floor for normal text.
const READABLE_TEXT_RATIO = 4.5;

const errorThemeOverride = (): Partial<Theme> => ({
  bgCell: volcaColor("--danger-subtle"),
  textDark: volcaColor("--danger"),
  accentColor: volcaColor("--danger"),
});

const overrideAppliedMarker = () => volcaColor("--blue-10");
const overrideReviewMarker = () => volcaColor("--amber-9");

export const manualRowMarkerColor = (): string => overrideAppliedMarker();

export const overrideMarkerColor = (
  override: IAiSpreadsheetCellOverride,
): string =>
  override.stale ||
  override.outcome === AI_SPREADSHEET_OVERRIDE_OUTCOME.needsReview
    ? overrideReviewMarker()
    : overrideAppliedMarker();

const normalizeColor = (value?: string | null): string | undefined => {
  if (!value) return undefined;

  const hex = value.replace(/^#/, "");

  if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex}`;

  if (/^[0-9a-fA-F]{8}$/.test(hex)) {
    const alpha = parseInt(hex.slice(0, 2), 16) / 255;
    const r = parseInt(hex.slice(2, 4), 16);
    const g = parseInt(hex.slice(4, 6), 16);
    const b = parseInt(hex.slice(6, 8), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return undefined;
};

export const getCellFormatType = (
  cell: IAiSpreadsheetCell,
): AiSpreadsheetValueType => {
  const code = cell.format?.format_code ?? "";

  if (code.includes("$")) return AI_SPREADSHEET_VALUE_TYPE.currency;
  if (code.includes("%")) return AI_SPREADSHEET_VALUE_TYPE.percent;
  if (/[dmy]/i.test(code)) return AI_SPREADSHEET_VALUE_TYPE.date;

  return isAiSpreadsheetNumericValueType(cell.value_type)
    ? AI_SPREADSHEET_VALUE_TYPE.number
    : AI_SPREADSHEET_VALUE_TYPE.text;
};

export const hasVisibleValue = (cell: IAiSpreadsheetCell | undefined) =>
  cell ? getAiSpreadsheetCellDisplayValue(cell) !== "" : false;

export const getCopyValue = (cell: IAiSpreadsheetCell) =>
  getAiSpreadsheetCellDisplayValue(cell);

export const getInCellEditValue = (cell: IAiSpreadsheetCell) => {
  if (isFormulaCell(cell)) {
    return cell.raw_input ?? "";
  }

  return getAiSpreadsheetCellDisplayValue(cell);
};

export const getContentAlign = (
  cell: IAiSpreadsheetCell,
): CellContentAlign | undefined => {
  const horizontal = cell.format?.alignment?.horizontal;

  return horizontal === "left" ||
    horizontal === "center" ||
    horizontal === "right"
    ? horizontal
    : undefined;
};

/**
 * The authored text colour if it reads on `background`, else ink that does.
 * A workbook picks its colours on white, so an authored colour also stays
 * wherever it reads at least as well as it did there: Excel's red is under
 * 4.5:1 on white, and light mode shouldn't turn it black.
 */
export const readableTextColor = (
  background: string,
  authored: string | undefined,
): string => {
  // A translucent fill shows the grid through it; measure what is drawn.
  const drawn = flattenColor(background, volcaColor("--surface"));
  if (authored) {
    const contrast = contrastRatio(authored, drawn);
    if (
      contrast >= READABLE_TEXT_RATIO ||
      contrast >= contrastRatio(authored, WORKBOOK_PAPER)
    ) {
      return authored;
    }
  }
  return pickContrastingColor(
    drawn,
    [volcaColor("--ink")],
    READABLE_TEXT_RATIO,
  );
};

export const buildThemeOverride = (
  cell: CachedSpreadsheetCell,
): Partial<Theme> | undefined => {
  const override: Partial<Theme> = {};
  // Spreadsheet::FormatContract: first nonblank color alias wins.
  const bgColor = normalizeColor(
    [
      cell.format?.background_color,
      cell.format?.bg_color,
      cell.format?.fill_color,
    ].find((value) => value?.trim()),
  );
  const fgColor = normalizeColor(
    [cell.format?.font_color, cell.format?.fg_color].find((value) =>
      value?.trim(),
    ),
  );
  const errorCode = getCellErrorCode(cell);

  if (bgColor) override.bgCell = bgColor;
  // A workbook styles its cells for a white sheet. When it sets both colours,
  // draw them as authored. When the theme supplies one, the pair can stop
  // reading in dark mode (near-white ink on a pale fill, black text on the dark
  // grid), so the text falls back to ink that reads.
  if (bgColor && fgColor) override.textDark = fgColor;
  else if (bgColor || fgColor) {
    override.textDark = readableTextColor(
      bgColor ?? volcaColor("--surface"),
      fgColor,
    );
  }
  if (cell.format?.bold) override.baseFontStyle = "600 13px";

  const merged =
    errorCode || cell.manual_row_save_failed
      ? { ...override, ...errorThemeOverride() }
      : override;

  return Object.keys(merged).length > 0 ? merged : undefined;
};
